// src/screens/pass/PassWelcomeStepView.jsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Center,
  Container,
  Paper,
  Space,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

import { authenticateSitePass } from "../../atoms/passNetwork";

const S = {
  fullHeight: { minHeight: "100vh" },
  bodyWrap: { paddingTop: 12, paddingBottom: 100 }, // match SmartLockPage spacing vibe
  paperMax: { maxWidth: 480, marginInline: "auto", textAlign: "left" },
};

export default function PassWelcomeStepView({
  HeaderLogo, // ✅ NEW: passed in from PassDetailsPage
  siteId,
  imageUrl, // kept for backwards compat; HeaderLogo should already include background
  siteTitle,
  itemTitle,
  onContinue,
  onRequireOTP,
}) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isChecking, setIsChecking] = useState(false);

  const niceTitle = useMemo(() => {
    const t = siteTitle || "";
    return t ? `Welcome to ${t}` : "Welcome";
  }, [siteTitle]);

  async function onNext() {

    if (!name) {
      setErrorMsg("Your name is required.");
      return;
    }

    if (!phone) {
      setErrorMsg("Phone number is required.");
      return;
    }

    setErrorMsg("");
    setIsChecking(true);

    try {
      const res = await authenticateSitePass({
        siteId,
        name: name.trim(),
        phoneE164: phone,
      });

      const ok = Number(res?.status) === 1;
      const data = res?.data || {};
      const authed = !!data?.auth;
      const banned = !!data?.is_banned;

      if (!ok) throw new Error(data?.message || "Couldn’t verify your account. Please try again.");

      if (banned) {
        setErrorMsg(
          "Your account has been restricted by West Coast Council. Please call 03 6472 6800 for assistance."
        );
        return;
      }

      if (authed) onContinue({ name: name.trim(), phoneE164: phone });
      else onRequireOTP({ name: name.trim(), phoneE164: phone });
    } catch (e) {
      setErrorMsg(e?.message || "Couldn’t verify your account. Please try again.");
    } finally {
      setIsChecking(false);
    }
  }

  return (
    <Container fluid px={0} style={S.fullHeight}>
      {/* ✅ Use the SmartLock-style header if provided */}
      {HeaderLogo ? HeaderLogo : null}

      {/* fallback if HeaderLogo not passed (won't be used once wired) */}
      {!HeaderLogo && imageUrl ? (
        <Box style={{ width: "100%", height: 220, overflow: "hidden" }}>
          <img
            src={imageUrl}
            alt=""
            style={{ width: "100%", height: 220, objectFit: "cover", display: "block" }}
          />
        </Box>
      ) : null}

      <Center style={S.bodyWrap}>
        <Box style={{ textAlign: "center", width: "100%" }}>
          <Title order={3}>{niceTitle}</Title>
          {/* <Text size="sm" c="dimmed">
            {itemTitle || "Camping Pass"}
          </Text> */}

          <Space h={28} />

          <Paper ml="10" mr={10} p="20" sx={S.paperMax} radius="lg">
            <TextInput
              placeholder="Enter Name"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              mb="sm"
            />

            <PhoneInput
              international
              defaultCountry="AU"
              countryCallingCodeEditable={false}
              placeholder="Enter phone number"
              value={phone}
              onChange={setPhone}
              inputComponent={TextInput}
              inputProps={{ label: "Phone number", mb: "md" }}
            />

            {errorMsg ? (
              <Text c="red" size="sm" mb="md">
                {errorMsg}
              </Text>
            ) : null}

            <Button fullWidth size="md" loading={isChecking} onClick={onNext} mt={20}>
              Continue
            </Button>

            <Text size="xs" style={{ color: "#999", marginTop: 12, lineHeight: 1.4 }}>
              By continuing, you agree to our{" "}
              <a href="https://www.autech.com.au/unlockwc-privacy" target="_blank" rel="noreferrer">
                Privacy Policy
              </a>{" "}
              and{" "}
              <a href="https://www.autech.com.au/unlockwc-tc" target="_blank" rel="noreferrer">
                T&amp;Cs
              </a>
              .
            </Text>
          </Paper>
        </Box>
      </Center>
    </Container>
  );
}
